<script setup lang="ts">
import { computed } from 'vue'
import { useForm11 } from '@/composables/useForm11'

const { pension, maxPension } = useForm11()

const progress = computed(() => {
  if (!maxPension.value) return 0
  return (pension.value / maxPension.value) * 100
})

const strokeDasharray = computed(() => {
  const circumference = 2 * Math.PI * 63 * 0.5 // 60% of the circle, increased radius to 63
  return `${(progress.value * circumference) / 100} ${circumference}`
})

function formatMoney(amount: number) {
  return new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount)
}
</script>

<template>
  <div
    class="flex flex-col items-center justify-center py-4 -mb-28 -mt-12 pointer-events-none"
  >
    <div class="relative">
      <svg class="w-full h-full max-w-96 max-h-96" viewBox="-20 -20 180 180">
        <!-- Gradient definitions -->
        <defs>
          <linearGradient
            id="progressGradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" style="stop-color: #6366f1" />
            <stop offset="100%" style="stop-color: #818cf8" />
          </linearGradient>
        </defs>

        <!-- Background arc -->
        <g>
          <path
            d="M 70 70 m -63, 0 a 63,63 0 1,1 126,0"
            stroke="#E5E7EB"
            stroke-width="24"
            fill="none"
            class="opacity-50"
          />
        </g>

        <!-- Progress arc -->
        <g>
          <path
            d="M 70 70 m -63, 0 a 63,63 0 1,1 126,0"
            stroke="url(#progressGradient)"
            stroke-width="24"
            fill="none"
            :stroke-dasharray="strokeDasharray"
            class="transition-all duration-700 ease-out drop-shadow-lg"
          />
        </g>

        <!-- Min and Max labels -->
        <text
          x="2"
          y="83"
          class="fill-gray-500"
          text-anchor="start"
          font-size="8"
        >
          €0
        </text>
        <text
          x="147"
          y="83"
          class="fill-gray-500"
          text-anchor="end"
          font-size="8"
        >
          {{ formatMoney(maxPension) }}
        </text>
      </svg>
      <div
        class="absolute inset-0 flex flex-col items-center justify-center text-center -mt-8"
      >
        <span class="text-3xl sm:text-4xl font-bold text-indigo-600">
          {{ formatMoney(pension) }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
path {
  transition: stroke-dasharray 0.7s ease-out;
}
</style>
